/**
 * Reading sections. `cms.get()` is this.
 */
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { SectionStatus, CONTENT_STALE_MS, permissionNotGranted } from '@framework/core';
import { useLang } from '../lang/lang-store';
import { sectionKey } from './cache-keys';
import { sectionLoader } from './section-loader';
import { useCapabilities } from './capability-store';
import type { CachedSection, SectionHandle, SectionMap } from './types';

/**
 * Read one section, or several at once.
 *
 *   get('hero')              → SectionHandle
 *   get(['hero', 'header'])  → { hero: …, header: … }
 *
 * Either way it is ONE network request: the loader batches every call made in the
 * same tick, so asking for ten costs the same round trip as asking for one.
 *
 * MUST be called at the top level of a component — it is a hook.
 */
export function useSection<K extends string>(name: K): SectionHandle<unknown>;
export function useSection<K extends string>(names: readonly K[]): SectionMap<K>;
export function useSection<K extends string>(
  input: K | readonly K[],
): SectionHandle<unknown> | SectionMap<K> {
  const lang = useLang();
  const qc = useQueryClient();
  const caps = useCapabilities();

  const many = Array.isArray(input);
  const names = (many ? input : [input]) as readonly K[];

  // Always useQueries — one hook call regardless of how many names, so hook order
  // stays stable whether a call site asks for one section or ten.
  const results = useQueries({
    queries: names.map((name) => ({
      queryKey: sectionKey(name, lang),
      staleTime: CONTENT_STALE_MS,
      queryFn: async (): Promise<CachedSection> => {
        const prev = qc.getQueryData<CachedSection>(sectionKey(name, lang));
        const r = await sectionLoader.load(name, lang, prev?.version);

        // CURRENT ⇒ no payload was sent: our copy is already the latest.
        if (r.status === SectionStatus.Current && prev) return prev;

        if (r.status === SectionStatus.Modified) {
          return { version: r.version ?? 0, data: r.data ?? {} };
        }

        // FORBIDDEN / NOT_FOUND are answers, not failures.
        return {
          version: 0,
          data: {},
          denied: permissionNotGranted(
            name,
            r.status === SectionStatus.NotFound ? 'NOT_FOUND' : 'FORBIDDEN',
          ),
        };
      },
    })),
  });

  const handles: SectionHandle<unknown>[] = results.map((q, i) => ({
    // Capability comes from the session-scoped store, never from the cached entry.
    canWrite: caps[names[i]] ?? false,
    data: q.data?.denied ? null : q.data?.data ?? null,
    denied: q.data?.denied ?? null,
    version: q.data?.version ?? 0,
    loading: q.isLoading,
    error: q.error,
  }));

  if (!many) return handles[0];
  return Object.fromEntries(names.map((n, i) => [n, handles[i]])) as SectionMap<K>;
}
